/** @jsxImportSource @emotion/react */
import { Paper as MuiPaper } from '@mui/material';
import { Buttons } from '@zip/business-components';
import { Constants } from 'global';
import { FC } from 'react';
import { MerchantApplicationStatus } from 'types/responses/activation';
import { PostApplicationSubmissionProps } from './PostApplicationSubmission';
import * as styles from './PostApplicationSubmission.styles';

const PostApplicationSubmissionModule: FC<PostApplicationSubmissionProps> = ({
  status,
  salesforceSdocContractUrl,
}) => {
  const onClickViewAndSign = () => {
    if (salesforceSdocContractUrl) {
      window.open(salesforceSdocContractUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {(status === MerchantApplicationStatus.Submitted ||
        (status === MerchantApplicationStatus.Approved &&
          !salesforceSdocContractUrl)) && (
        <MuiPaper
          elevation={0}
          css={styles.statusOutcome}
          data-testid="post-submission-under-review-state"
        >
          <img
            src={Constants.assets.signUpAssets.illustrations.transparentTerms}
            width={64}
            height={64}
            title="Document Review"
            alt="Document review"
          />

          <div className="text-content">
            <span className="heading1" data-testid="under-review-heading">
              Hold tight! We are reviewing your account details
            </span>
            <span className="body2" data-testid="under-review-body">
              Once we have reviewed your account and you are approved, we will
              start preparing your contract. You should hear from us within{' '}
              <strong className="body1">one business day.</strong>
            </span>
          </div>
        </MuiPaper>
      )}

      {status === MerchantApplicationStatus.Approved &&
        salesforceSdocContractUrl && (
          <MuiPaper
            elevation={0}
            css={styles.statusOutcome}
            data-testid="post-submission-contract-ready-state"
          >
            <img
              src={Constants.assets.signUpAssets.illustrations.signDocs}
              width={64}
              height={64}
              title="Sign Docs"
              alt="Sign Docs"
            />

            <div className="text-content">
              <span className="heading1" data-testid="contract-heading">
                Your contract is ready
              </span>
              <span className="body2" data-testid="contract-body">
                Please review and sign the Letter of Offer (LOO) and General
                Terms & Conditions, which together form Merchant Service
                Agreement.
              </span>
            </div>

            <Buttons.Primary
              onClick={onClickViewAndSign}
              aria-label="View and sign your merchant service agreement contract"
              data-testid="view-sign-button"
            >
              View & sign
            </Buttons.Primary>
          </MuiPaper>
        )}

      {status === MerchantApplicationStatus.Declined && (
        <div
          css={styles.declinedOutcome}
          data-testid="post-submission-declined-state"
        >
          <img
            src={Constants.assets.signUpAssets.illustrations.laptop}
            width={96}
            height={96}
            title="Laptop Declined"
            alt="Application Declined"
          />
          <span className="heading1" data-testid="declined-heading">
            Unfortunately your business isn't eligible for Zip right now
          </span>
          <span className="body2" data-testid="declined-body">
            When assessing the suitability of a merchant we consider a range of
            factors. Thank you for your consideration and we wish you all the
            best.
          </span>
        </div>
      )}
    </>
  );
};

export default PostApplicationSubmissionModule;
